import { format } from 'date-fns';
import ru from 'date-fns/locale/ru';
import _ from 'lodash';
import { ListFilter } from 'lucide-react';
import { StyledGameCard, StyledGameDateHeader } from 'modules/games/ui/components/games-list/games-list';
import { fromMatchOverlayState } from 'modules/devices/domain/constants';
import { StepperMainTool } from 'modules/main/domain/utils/stepper-tool';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';
import { useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { SUPPORT_URL } from 'shared/constants/links';
import { AppDispatch } from 'store/store';
import UDHeaderTitle from 'ud-ui/components/header-title';
import UDText from 'ud-ui/components/text';
import UDTippy from 'ud-ui/components/tippy';
import { useMainLayout } from 'modules/app-shell/ui/layout/context';
import { setTournamentId } from 'modules/schedule/store';
import { fetchCalendar } from 'modules/schedule/store/actions';
import { selectFilter } from 'modules/schedule/store/selectors';
import DataPicker from 'modules/schedule/ui/components/data-picker';
import { useSchedule } from 'modules/schedule/ui/hooks/use-schedule';
import useFilterModal from './modals/filters';
import { ChampionshipHeader, FilterButton, GamesList, GamesWrapper, MainBlock } from './styles';
import { theme } from 'styles/theme/theme';

function Calendar() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { info, isLoading: isLoadingOrganizer } = useOrganizer();
  const { isLoading: isLoadingSchedule, isLoaded: isLoadedSchedule, selectDay, currentDate, tournamentId } = useSchedule();
  const filter = useSelector(selectFilter);
  const { setHeaderContent } = useMainLayout();

  useLayoutEffect(() => {
    StepperMainTool.setByPath('stepper:main:manageTournament', true);
  }, []);

  useEffect(() => {
    if (!info) {
      return;
    }
    if (isLoadedSchedule && info.id === tournamentId) {
      return;
    }
    dispatch(setTournamentId({ id: info.id }));
    dispatch(
      fetchCalendar({
        tournamentId: info.id,
        monthNum: currentDate.month,
        yearNum: currentDate.year,
      }),
    );
  }, [currentDate.month, currentDate.year, dispatch, info, isLoadedSchedule, tournamentId]);

  const dateKey = useMemo(() => {
    return format(new Date(selectDay.date), 'dd MMMM, EEEEEE', { locale: ru });
  }, [selectDay.date]);

  // Ссылка на табло больше не строится по матчу — она принадлежит устройству,
  // поэтому кнопка ведёт на «Устройства», где эту ссылку и копируют.
  const onShowGameTemplatesClick = useCallback(() => {
    navigate(Pages.DEVICES.INDEX, { state: fromMatchOverlayState });
  }, [navigate]);

  const matches = useMemo(() => {
    return _(selectDay.matches).groupBy('championship.name').value();
  }, [selectDay.matches]);

  const matchesByFilter = useMemo(() => {
    const championshipName = filter.championship;
    return championshipName === 'all' ? matches : matches[championshipName] ? { [championshipName]: matches[championshipName] } : {};
  }, [filter.championship, matches]);

  const { open, modal } = useFilterModal();

  useEffect(() => {
    setHeaderContent(<UDHeaderTitle>Расписание</UDHeaderTitle>);
    return () => setHeaderContent(null);
  }, [setHeaderContent]);

  return (
    <>
      <MainBlock>
        <div className={'flex'} style={{ height: 279, gap: 30, justifyContent: 'space-between' }}>
          <DataPicker />
        </div>
        {!(isLoadingOrganizer || isLoadingSchedule) &&
          (Object.keys(matchesByFilter).length > 0 ? (
            <div className="mb-6 w-full last:mb-0" style={{ overflow: 'hidden' }}>
              <StyledGameDateHeader
                style={{
                  justifyContent: 'space-between',
                  display: 'flex',
                  flexDirection: 'row',
                }}
              >
                <p>{dateKey}</p>
                <UDTippy content={'Фильтр'} placement={'top'}>
                  <FilterButton onClick={open}>
                    <ListFilter size={16} />
                  </FilterButton>
                </UDTippy>
              </StyledGameDateHeader>
              <GamesWrapper>
                {_.map(matchesByFilter, (value, key) => (
                  <GamesList>
                    <ChampionshipHeader className={'ChampionshipHeader'}>
                      <UDText type={'subhead'} style={{ fontWeight: 'bold' }}>
                        {key}
                      </UDText>
                    </ChampionshipHeader>
                    {value.map((game) => (
                      <StyledGameCard
                        key={game.id}
                        game={game}
                        championshipId={game.championship.id}
                        onShowTemplatesClick={onShowGameTemplatesClick}
                      />
                    ))}
                  </GamesList>
                ))}
              </GamesWrapper>
            </div>
          ) : (
            <div
              className={'flex-center w-full'}
              style={{
                flexDirection: 'column',
                backgroundColor: 'white',
                paddingBlock: 26,
                borderRadius: 8,
                height: 'fit-content',
                position: 'relative',
              }}
            >
              <FilterButton
                style={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  backgroundColor: theme.colors.SurfaceCultured10,
                }}
                onClick={open}
              >
                <ListFilter size={16} />
              </FilterButton>
              <UDText type={'callout'} className={'text-surfaceRomanSilver30'} style={{ fontSize: 15 }}>
                На эти даты матчей нет.
              </UDText>
              <UDText type={'caption-1'}>
                <a style={{ color: theme.colors.EditorAccentBlue }} target={'_blank'} href={SUPPORT_URL} rel="noreferrer">
                  Сообщить об ошибке
                </a>
              </UDText>
            </div>
          ))}
      </MainBlock>
      {modal}
    </>
  );
}

export default Calendar;
